<template>
  <div class="h-fit relative rounded-[3px]">
    <!-- Chart Toolbar -->
    <div
      class="w-full flex justify-between items-center bg-slate-100 border-b border-slate-300 p-2"
    >
      <span
        v-if="!readOnly"
        data-test="edit-icon"
        class="flex cursor-pointer w-8 h-8 p-[6px] rounded inline-flex justify-center items-center gap-1.5 overflow-hidden"
        data-intercom-target="edit button"
        @click="$emit('openEditDialog')"
      >
        <el-tooltip
          :content="t('charts.chartHeaders.editChart')"
          placement="bottom"
          popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
        >
          <font-awesome-icon
            :icon="['far', 'pen']"
            class="text-slate-800 w-5 h-5 focus:outline-none"
          />
        </el-tooltip>
      </span>
      <div>
        <span
          v-if="!readOnly"
          class="absolute right-1/2 cursor-grab drag-handle w-8 h-8 p-[6px] rounded inline-flex justify-center items-center gap-1.5 overflow-hidden"
        >
          <el-tooltip
            :content="t('charts.chartHeaders.clickAndHold')"
            placement="bottom"
            popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
          >
            <font-awesome-icon
              :icon="['far', 'grip-dots']"
              class="text-slate-600 w-4 h-4 focus:outline-none"
            />
          </el-tooltip>
        </span>
        <span class="flex gap-[6px] items-center">
          <span
            class="cursor-pointer w-8 h-8 p-[6px] rounded inline-flex justify-center items-center gap-1.5 overflow-hidden"
            data-test="download-options"
          >
            <ClientOnly>
              <el-popover
                popper-class="content-popover"
                trigger="click"
                width="auto"
                placement="bottom-end"
                :transition="'el-fade-in-linear'"
                :show-arrow="false"
              >
                <template #reference>
                  <el-button
                    class="custom-button bg-transparent"
                    data-intercom-target="download button"
                  >
                    <el-tooltip
                      :content="t('charts.chartHeaders.downloadChart')"
                      placement="bottom"
                      popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
                    >
                      <font-awesome-icon
                        class="text-slate-800 bg-transparent w-5 h-5 focus:outline-none"
                        :icon="['far', 'arrow-down-to-line']"
                      />
                    </el-tooltip>
                  </el-button>
                </template>
                <ul
                  class="p-2 rounded bg-white border border-[#e5e7eb] shadow-popover"
                >
                  <li
                    class="context-menu-option"
                    data-intercom-target="download chart image button"
                    data-test="download-chart-image"
                    @click="$emit('downloadChartImage')"
                  >
                    {{ t("charts.downloadChartImage") }}
                  </li>
                  <li
                    class="context-menu-option"
                    data-intercom-target="download chart data button"
                    data-test="download-chart-data"
                    @click="$emit('downloadChartData')"
                  >
                    {{ t("charts.downloadChartData") }}
                  </li>
                </ul>
              </el-popover>
            </ClientOnly>
          </span>
          <span
            v-if="!readOnly"
            class="cursor-pointer w-8 h-8 p-[6px] rounded inline-flex justify-center items-center gap-1.5 overflow-hidden"
            data-intercom-target="remove chart button"
            data-test="remove-chart"
            @click="handleChartRemoval"
          >
            <el-tooltip
              :content="chartRemovalTooltip"
              placement="bottom"
              popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
            >
              <font-awesome-icon
                :icon="['far', 'circle-minus']"
                class="text-slate-800 w-5 h-5 focus:outline-none"
              />
            </el-tooltip>
          </span>
          <span
            v-if="!readOnly"
            class="cursor-pointer w-10 h-8 px-[2px] pb-[2px] rounded inline-flex justify-start items-center gap-1.5 overflow-hidden"
            data-intercom-target="chart width button"
            data-test="resize-options"
          >
            <ClientOnly>
              <el-popover
                v-model:visible="showResizePopover"
                popper-class="content-popover"
                trigger="click"
                width="auto"
                placement="bottom"
                :transition="'el-fade-in-linear'"
                :show-arrow="false"
              >
                <template #reference>
                  <div>
                    <el-tooltip
                      :content="t('charts.chartHeaders.chartWidth')"
                      placement="bottom"
                      popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
                    >
                      <ChartSizeIcon :size="chartSize" />
                    </el-tooltip>
                  </div>
                </template>
                <ul
                  class="p-2 rounded bg-white border border-[#e5e7eb] shadow-popover"
                >
                  <li
                    v-for="option in chartSizeOptions"
                    :key="option.key"
                    :data-intercom-target="option.intercomTarget"
                    :data-test="'size-option-' + option.size"
                    class="context-menu-option !gap-8"
                    @click.stop="handleSizeChange(chartId, option.size)"
                  >
                    <ChartSizeIcon :size="option.size" />
                    {{ t(option.key) }}
                  </li>
                </ul>
              </el-popover>
            </ClientOnly>
          </span>
        </span>
      </div>
    </div>
    <div class="px-3">
      <div class="w-full flex justify-center py-4">
        <div class="flex items-center gap-2 max-w-full overflow-hidden">
          <!-- Chart Title -->
          <div class="truncate font-body-2" :data-target="dataText">
            {{ title }}
          </div>
          <div class="flex flex-row items-center">
            <el-tooltip
              :content="tooltipText"
              placement="top"
              popper-class="text-white font-sans text-sm font-normal py-1 px-[10px] bg-[#20232d] rounded-md shadow-tooltip"
            >
              <font-awesome-icon
                class="w-4 h-4 text-secondary-600 focus:outline-none"
                :icon="['far', 'circle-info']"
              />
            </el-tooltip>
          </div>
        </div>
      </div>

      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";
import ChartSizeIcon from "./ChartSizeIcon.vue";
import { useChartsStore } from "~/store/charts.ts";

const { t } = useI18n();

const chartsStore = useChartsStore();

const emit = defineEmits([
  "changeChartSize",
  "downloadChartImage",
  "downloadChartData",
  "openEditDialog",
  "removeChart",
]);

defineProps({
  chartId: {
    type: Number,
    required: true,
  },
  chartSize: {
    type: Number,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: true,
  },
  dataText: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  tooltipText: {
    type: String,
    required: true,
  },
});

const showResizePopover: Ref<Boolean> = ref(false);

const chartSizeOptions = [
  {
    key: "charts.singleColumnWidth",
    size: 1,
    intercomTarget: "single-column-width-btn",
  },
  {
    key: "charts.doubleColumnWidth",
    size: 2,
    intercomTarget: "double-column-width-btn",
  },
  {
    key: "charts.fullWidth",
    size: 3,
    intercomTarget: "full-column-width-btn",
  },
];

const allowChartRemoval = computed(() => {
  const charts = chartsStore.analyticsCharts.get(
    chartsStore.selectedChartTemplate?.id,
  );

  return charts?.length > 1;
});

const chartRemovalTooltip = computed(() => {
  return allowChartRemoval.value
    ? t("charts.chartHeaders.removeChart")
    : t("charts.chartHeaders.disabledRemoveChart");
});

const handleChartRemoval = () => {
  if (allowChartRemoval.value) {
    emit("removeChart");
  }
};

const handleSizeChange = (chartId: Number, size: Number) => {
  showResizePopover.value = false;
  emit("changeChartSize", { chartId, size });
};
</script>
